import {
  getFormattedListTimeZones,
  getTimeZoneFromFormattedList,
} from 'app/ProviderSearch/utils'
import Select from 'react-select'
import { TimeZoneContext } from 'context/common/TimeZoneContext'
import { useContext, useMemo, useState } from 'react'
import { minimalTimezoneSet } from 'constants/common'
import { Placement } from 'react-bootstrap/esm/Overlay'
import classNames from 'classnames'
import styles from './index.module.scss'
import { customStyles } from './styles'
import { BLACK_PEARL, LIGHT_GREY, WHITE } from 'constants/colors'

type TimeZonePickerProps = {
  className?: string
  containerClass?: string
  timezoneInfoClass?: string
  tooltipDirection?: Placement
  showClientMsgHint?: boolean
  showToolTip?: boolean
}

const TimeZonePicker: React.FC<TimeZonePickerProps> = ({
  className = '',
  containerClass,
  timezoneInfoClass = 'justify-content-end',
  showClientMsgHint = true,
}) => {
  const { setTimeZone, timeZoneObject, setTimeZoneObject } = useContext(
    TimeZoneContext
  )

  const [menuPlacement, setMenuPlacement] = useState('bottom')

  const timeZoneSelectItem = useMemo(() => {
    if (timeZoneObject) {
      return getTimeZoneFromFormattedList(
        timeZoneObject.tzName,
        timeZoneObject.tzCode
      )
    }

    return null
  }, [timeZoneObject])

  const onChangeTimeZone = ({ value, tzCode }) => {
    const timeZoneObject = minimalTimezoneSet.find(
      (timeZoneObject) =>
        timeZoneObject.tzName === value && timeZoneObject.tzCode === tzCode
    )
    setTimeZone(timeZoneObject.tzCode)
    setTimeZoneObject(timeZoneObject)
    sessionStorage.setItem(sessionStorage.TIMEZONE_ABBR, timeZoneObject.tzAbbr)
    /** When timezone is set in Provider search page, adding tz abbreviation to session storage.
     ** This key will be used when navigating to provider profile page and will be added as a query param.
     ** On provider profile page, if this key is available, then the timezone in timezone picker will be set as that else practice's lat, lng will be used.
     ** We're doing this because we want the timezone that is set on Provider search page should be shown on Provider profile.
     **/
  }

  const isOptionSelected = (
    option: TimezoneSelectItem,
    selectValue: TimezoneSelectItem[]
  ): boolean => {
    const selectedValue = selectValue[0]
    const doesCodeMatch = option.tzCode === selectedValue?.tzCode
    const doesNameMatch = option.value === selectedValue?.value
    return doesCodeMatch && doesNameMatch
  }

  const timeZoneOptions = useMemo(() => {
    return getFormattedListTimeZones()
  }, [])

  //TODO: Restructure component
  return (
    <div
      data-testid="timezone-selector"
      className={classNames(`${containerClass} ${styles.TimeZonePicker}`)}
    >
      {showClientMsgHint && (
        <div className={`${styles.ClientMessage} ${timezoneInfoClass}`}>
          <div className={styles.ClientMessage_text}>
            Clients will be booked in this timezone
          </div>
        </div>
      )}
      <Select
        aria-label="timezone selector"
        isOptionSelected={isOptionSelected}
        cacheOptions
        inputId="timezone-picker"
        placeholder="Timezone"
        className={classNames(`${styles.TimeZonePicker_input}  ${className}`)}
        value={timeZoneSelectItem}
        onChange={onChangeTimeZone}
        options={timeZoneOptions}
        menuPlacement="auto"
        theme={(theme) => ({
          ...theme,
        })}
        styles={{
          ...customStyles,
          control: (baseStyles, state) => {
            const { menuIsOpen } = state
            return {
              ...baseStyles,
              border: `1px solid ${LIGHT_GREY}`,
              boxShadow: 'none',
              borderRadius: '100px',
              backgroundColor: WHITE,
              height: '50px',
              '&:hover': {
                border: `1px solid ${LIGHT_GREY}`,
              },
              ...(menuIsOpen && {
                borderRadius:
                  menuPlacement === 'top'
                    ? '0px 0px 25px 25px'
                    : '25px 25px 0px 0px',
                transition: 'border-radius 0.1s ease-out',
              }),
            }
          },
          menu: (baseStyles, state) => {
            // since state.placement value is not avaliable for "control" element, had to create seperate state
            if (state.placement) {
              setMenuPlacement(state.placement)
            }

            return {
              ...baseStyles,
              color: BLACK_PEARL,
              margin: '0px',
              border: `1px solid ${LIGHT_GREY}`,
              borderTop: 'none',
              fontWeight: '400',
              boxShadow:
                menuPlacement === 'top'
                  ? '0px -3px 6px 0px rgba(33, 37, 41, 0.12)'
                  : '0px 3px 6px 0px rgba(33, 37, 41, 0.12)',
              borderRadius:
                menuPlacement === 'top'
                  ? '25px 25px 0px 0px'
                  : '0px 0px 25px 25px',
              overflow: 'hidden',
            }
          },
          menuList: (baseStyles) => {
            return {
              ...baseStyles,
              borderRadius:
                menuPlacement === 'top'
                  ? '25px 25px 0px 0px'
                  : '0px 0px 25px 25px',
              paddingBottom: '0px',
              paddingTop: '0px',
            }
          },
        }}
        components={{
          IndicatorSeparator: () => null,
        }}
      />
    </div>
  )
}

export default TimeZonePicker
